import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { ErrorCodes } from 'src/data/error-codes';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { useFileService } from '../api/useFileServices';
import { FileState, HeaderData } from '../types/file';

export interface FileStoreState {
  files: {
    [key: string]: FileState | undefined;
  };

  isRenameFileOpened: boolean;
  setIsRenameFileOpened: (value: boolean) => void;

  isSticky: boolean;
  setIsSticky: (value: boolean) => void;

  isVersionHistoryOpen: boolean;
  fetchFile: ({ id, isPublic }: { id?: string; isPublic?: boolean }) => Promise<void>;
  onUpdatePublicFilePassword: ({
    id,
    password,
  }: {
    id?: string;
    password?: string;
  }) => Promise<void>;
  setFile: (fileId: string, file: FileState) => void;
  onPinFile: (fileId: string, pin: boolean) => void;
  onFavoriteFile: (fileId: string, favorite: boolean) => void;
  onResetFileOnboarding: (fileId: string, resetFileOnboarding: boolean) => void;
  onAddView: ({ fileId, type }: { fileId: string; type: string }) => Promise<void>;
  onAddTag: ({
    fileId,
    name,
    id,
    color,
  }: {
    name: string;
    id: string;
    color: string;
    fileId: string;
  }) => void;
  onDeleteTag: ({ fileId, id }: { fileId: string; id: string }) => void;
  onUpdateTag: ({
    name,
    id,
    color,
    fileId,
    mode,
  }: {
    name?: string;
    id?: string;
    color?: string;
    fileId: string;
    mode: string;
  }) => void;
  onDeleteView: ({ fileId, viewId }: { fileId: string; viewId: string }) => void;
  // onAddTag: ({}: {}) => void;
  onRenameView: ({
    fileId,
    viewId,
    name,
  }: {
    fileId: string;
    viewId: string;
    name: string;
  }) => void;
  onUpdateFileNameAndIcon: ({
    name,
    icon,
    fileId,
  }: {
    name?: string;
    icon?: any;
    fileId: string;
  }) => void;
  resetState: () => void;
  onImportCsvFile: ({
    workspace,
    fileTitle,
    tasknameIndex,
    fileData,
    fileHeader,
    fileType,
  }: {
    workspace?: string;
    fileTitle: string;
    tasknameIndex: number;
    fileData: [[string]];
    fileHeader: HeaderData[];
    fileType?: string;
  }) => void;
  onUpdateEmbedUrl: (fileId: string, viewId: string, url: string) => void;
  onUpdateViewConfig: ({
    fileId,
    viewId,
    config,
    isPrimaryFile,
  }: {
    fileId: string;
    viewId: string;
    config: any;
    isPrimaryFile: boolean;
  }) => Promise<any>;
}
const getInitialValue = () => ({
  files: {},
});

const {
  fetchFileDetails,
  pinFile,
  favoriteFile,
  resetFileOnboarding,
  addFileView,
  deleteFileView,
  updateFileNameAndIcon,
  addTag,
  deleteTag,
  updateTag,
  importcsvFile,
  updateEmbedUrl,
  renameView,
  updatePublicFilePassword,
} = useFileService();

export const useFileStore = create<
  FileStoreState,
  [['zustand/devtools', FileStoreState], ['zustand/immer', FileStoreState]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      ...getInitialValue,

      setFile: (fileId, file) => {
        set((state) => {
          state.files[fileId] = file;
        });
      },
      fetchFile: async ({ id, isPublic }: { id: string; isPublic?: boolean }) => {
        set((state) => {
          const { files } = state;
          if (id && !files[id]) {
            files[id] = { isLoading: true };
          } else {
            files[id] = { ...files[id], isLoading: true };
          }
        });
        try {
          const response = await fetchFileDetails({ id, isPublic });
          if (response) {
            if (
              response === ErrorCodes['FILE_ACCESS_DENIED'] ||
              response === ErrorCodes['FILE_NOT_FOUND'] ||
              response === ErrorCodes['FILE_PERMISSION_DENIED']
            ) {
              set((state) => {
                state.files[id] = {
                  errorCode: response,
                  isLoading: false,
                  authorisation: false,
                  isPublicFilePasswordSet: false,
                };
              });
              return response;
            } else if (response === ErrorCodes['FILE_PASSWORD_REQUIRED']) {
              set((state) => {
                state.files[id] = {
                  errorCode: response,
                  isLoading: false,
                  authorisation: false,
                  isPublicFilePasswordSet: true,
                };
              });
              return response;
            } else if (response) {
              set((state) => {
                state.files[id] = {
                  ...response,
                  errorCode: null,
                  isLoading: false,
                  authorisation: true,
                  isPublicFilePasswordSet: false,
                };
              });
            }
          } else {
            set((state) => {
              state.files[id] = { isLoading: true };
            });
          }
        } catch (error) {
          console.error(error);
        }
      },
      onUpdateFileNameAndIcon: async ({ name, icon, fileId }) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];
          if (file) {
            if (file.name && name !== undefined) file.name = name || '';
            if (icon) file.icon = icon;
          }
        });
        const fileResponse = await updateFileNameAndIcon({
          name,
          icon,
          fileId,
        });

        if (fileResponse) {
          get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },

      onUpdatePublicFilePassword: async ({ id, password }: { id: string; password?: string }) => {
        set((state) => {
          const { files } = state;
          if (id && !files[id]) {
            files[id] = { isLoading: true, isPublicFilePasswordSet: false };
          }
        });
        const fileResponse = await updatePublicFilePassword({
          id,
          password,
        });

        if (fileResponse) {
          if (fileResponse !== 'incorrect-password') {
            if (fileResponse.publicPassword) {
              return fileResponse.publicPassword;
            }
          } else {
            return fileResponse;
          }
        }
      },
      onPinFile: async (fileId, pin) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];

          if (file) {
            file.pin = pin;
          }
        });
        const fileResponse = await pinFile({
          fileId,
          pin,
        });

        if (fileResponse) {
          get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },

      //Adds tag to organisation & file
      onAddTag: async ({ fileId, id, name, color }) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];

          if (file) {
            const tags = file?.tags || [];
            tags.push({ id, name, color });
            file.tags = tags;
          }
        });
        const fileResponse = await addTag({
          fileId,
          id,
          name,
          color,
        });

        if (fileResponse) {
          get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },
      //Deletes tag from organisation & file
      onDeleteTag: async ({ fileId, id }) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];

          if (file && file.tags) {
            const indexOfDeletedObject = file.tags?.findIndex((tag) => tag.id === id);
            file.tags.splice(indexOfDeletedObject, 1);
          }
        });
        const fileResponse = await deleteTag({
          fileId,
          id,
        });

        if (fileResponse) {
          get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },
      //Adds,removes,updates tags in files
      onUpdateTag: async ({ fileId, id, name, color, mode }) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];

          if (file && file.tags) {
            if (mode === 'edit') {
              const indexOfDeletedObject = file.tags?.findIndex((tag) => tag.id === id);
              file.tags[indexOfDeletedObject] = { id: id, name: name, color: color };
            } else if (mode === 'add') {
              file.tags.push({ id, name, color });
            } else if (mode === 'remove') {
              const indexOfDeletedObject = file.tags?.findIndex((tag) => tag.id === id);
              file.tags.splice(indexOfDeletedObject, 1);
            }
          }
        });
        const fileResponse = await updateTag({
          fileId,
          id,
          name,
          color,
          mode,
        });

        if (fileResponse) {
          get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },

      onFavoriteFile: async (fileId, favorite) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];
          if (file) {
            file.favorite = favorite;
          }
        });
        const fileResponse = await favoriteFile({
          fileId,
        });
        if (fileResponse) {
          get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },

      onResetFileOnboarding: async (fileId, fileOnboardingComplete) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];
          if (file) {
            file.resetFileOnboarding = fileOnboardingComplete;
          }
        });
        const fileResponse = await resetFileOnboarding({
          fileId,
        });
        if (fileResponse) {
          get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },

      onAddView: async ({ fileId, type }) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];
          if (file) {
            file.internalFiles?.push({
              type: type,
              id: '',
              view: '',
              name: '',
              viewId: '',
              config: {},
            });
          }
        });
        const fileResponse = await addFileView({
          fileId,
          type,
        });

        if (fileResponse && fileResponse !== 'Unauthorised access') {
          fileResponse && get().setFile(fileId, fileResponse);

          return fileResponse.internalFiles[fileResponse.internalFiles.length - 1];
        }
      },
      onUpdateViewConfig: async ({ fileId, viewId, config, isPrimaryFile }) => {
        const file = JSON.parse(JSON.stringify(get().files[fileId]));
        if (!file) return;
        if (isPrimaryFile) {
          file.primaryFile.config = config;
        } else {
          const view = file.internalFiles?.find((internalFile) => internalFile.viewId === viewId);
          if (view) {
            view.config = config;
          }
        }
        set((state) => {
          state.files[fileId] = file;
        });
        const fileResponse = await useFileService().saveViewConfiguration({
          fileId: fileId,
          viewId: isPrimaryFile ? '' : viewId,
          data: config,
        });
        return fileResponse;
      },
      onDeleteView: async ({ fileId, viewId }) => {
        const fileResponse = await deleteFileView({
          fileId,
          viewId,
        });

        if (fileResponse) {
          fileResponse && get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },

      onRenameView: async ({ fileId, viewId, name }) => {
        set(async (state) => {
          const { files } = state;
          const file = files[fileId];
          if (file) {
            if (file.primaryFile?.id === viewId) {
              file.primaryFile.name = name;
            } else if (file.internalFiles) {
              const internalFiles = [...file.internalFiles];
              let tempInternalFiles: any = [];

              tempInternalFiles = internalFiles.map((internalFile) => {
                if (internalFile.viewId === viewId) {
                  return { ...internalFile, name };
                } else {
                  return internalFile;
                }
              });

              files.internalFiles = tempInternalFiles;
            }
          }
        });
        const fileResponse = await renameView(fileId, viewId, name);
        if (fileResponse) {
          fileResponse && get().setFile(fileId, fileResponse);
          return fileResponse;
        }
      },

      onImportCsvFile: async ({
        workspace,
        fileTitle,
        tasknameIndex,
        fileData,
        fileHeader,
        fileType,
      }) => {
        const fileResponse = await importcsvFile({
          workspace,
          fileTitle,
          tasknameIndex,
          fileData,
          fileHeader,
          fileType: fileType || 'list',
        });
        if (fileResponse) {
          return fileResponse;
        }
      },

      onUpdateEmbedUrl: async (fileId, viewId, url) => {
        set((state) => {
          const { files } = state;
          const file = files[fileId];
          if (file) {
            if (file.primaryFile?.id === viewId && file.primaryFile.data) {
              file.primaryFile.data.url = url;
            } else if (file.internalFiles) {
              const internalFiles = [...file.internalFiles];
              let tempInternalFiles: any = [];
              const currentEmbed = internalFiles.find((view) => view.id === viewId);
              const filteredInternalFiles = internalFiles.filter((f) => f.id !== viewId);
              tempInternalFiles = [...filteredInternalFiles];
              if (currentEmbed) {
                if (currentEmbed.data) currentEmbed.data.url = url;
                tempInternalFiles.push(currentEmbed);
              }
              files.internalFiles = tempInternalFiles;
            }
          }
        });
        if (url) {
          const fileResponse = await updateEmbedUrl(viewId, url);
          if (fileResponse) {
            // get().setFile(fileId, fileResponse);
            return fileResponse;
          }
        }
      },

      isRenameFileOpened: false,
      setIsRenameFileOpened: (d) =>
        set((s) => {
          s.isRenameFileOpened = d;
        }),

      isSticky: false,
      setIsSticky: (value) =>
        set((s) => {
          s.isSticky = value;
        }),

      resetState: () => {
        set((s) => {
          return { ...s, ...getInitialValue() };
        });
      },
      isVersionHistoryOpen: false,
      ...getInitialValue(),
    }))
  )
);
